import React, {
  ChangeEventHandler,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CasesIcon,
  Field,
  FieldTypes,
  OrganizationIcon,
  SelectDrawer,
  Spacing,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  styled,
  useBreakpoints,
} from 'ui';
import { PageToolbarPortal } from '@components/PageHeaderToolbar/PageHeaderToolbar';
import { LayoutContext } from '@context/LayoutContext';
import { useUserOrgCase } from '@context/UserOrgCaseContext';

const StyledHeader = styled(Stack)`
  position: fixed;
  padding: ${({ theme }) => theme.spacing(Spacing.Medium)};
  padding-left: 86px;
  height: 88px;
  background: ${({ theme }) => theme.palette.paper[200]};
  box-shadow: ${({ theme }) => theme.boxShadows.card};
  z-index: 1000;
  color: ${({ theme }) => theme.palette.text.primary};
  width: 100%;
  margin-top: 56px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const SpeedDialWrapper = styled(SpeedDial)`
  position: fixed;
  bottom: 5%;
  right: 5%;
  z-index: 9999;
`;

export function SecondaryTopNavbar() {
  const {
    userOrganizations,
    userCases,
    selectedOrganization,
    selectedCase,
    handleOrganizationSelect,
    handleCaseSelect,
  } = useUserOrgCase();
  const navigate = useNavigate();
  const { isSpeedDialOpen, handleSpeedDialClose, handleSpeedDialOpen } =
    useContext(LayoutContext);
  const [isOrganizationsDrawerOpen, setIsOrganizationsDrawerOpen] =
    useState(false);
  const [isCasesDrawerOpen, setIsCasesDrawerOpen] = useState(false);
  const { isMdUp } = useBreakpoints();
  const isPermissionsUrl = location.pathname.includes('permissions');
  const isHistoryUrl = location.pathname.includes('history');

  const organizationItems = useMemo(
    () =>
      userOrganizations.map(({ organizationID, organizationName }) => ({
        key: organizationID,
        value: organizationName,
      })),
    [userOrganizations],
  );

  const caseItems = useMemo(
    () =>
      userCases.map(({ caseID, caseName }) => ({
        key: caseID,
        value: caseName,
      })),
    [userCases],
  );

  const handleOrganizationChange: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const inputOrganizationId = e.target.value;
    const inputOrganization = userOrganizations.find(
      ({ organizationID }) => organizationID === inputOrganizationId,
    );
    if (inputOrganization) {
      if (isPermissionsUrl) {
        navigate(`/permissions/organization${location.search}`);
      }
      handleOrganizationSelect(inputOrganization);
    }
  };

  const handleCaseChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputCaseId = e.target.value;
    const inputCase = userCases.find(({ caseID }) => caseID === inputCaseId);
    if (inputCase) {
      if (isPermissionsUrl) {
        navigate(`/permissions/case${location.search}`);
      }
      handleCaseSelect(inputCase);
    }
  };

  if (isMdUp) {
    return (
      <StyledHeader data-testid="secondary-header-container">
        <Box>
          <Field
            label="Organization"
            menuItems={organizationItems || []}
            mr={Spacing.Medium}
            onChange={handleOrganizationChange}
            type={'select' as FieldTypes}
            value={selectedOrganization?.organizationID || ''}
            width="256px"
          />
          <Field
            disabled={userCases.length === 0 || isHistoryUrl}
            label="Case"
            menuItems={caseItems || []}
            onChange={handleCaseChange}
            type={'select' as FieldTypes}
            value={selectedCase?.caseID || ''}
            width="256px"
          />
        </Box>
        <Box>
          <PageToolbarPortal />
        </Box>
      </StyledHeader>
    );
  }

  return (
    <>
      <SpeedDialWrapper
        ariaLabel="Utilities"
        icon={<SpeedDialIcon />}
        onClose={handleSpeedDialClose}
        onOpen={handleSpeedDialOpen}
        open={isSpeedDialOpen}
      >
        <SpeedDialAction
          icon={<OrganizationIcon />}
          onClick={() => {
            setIsOrganizationsDrawerOpen(true);
          }}
          tooltipTitle="Organization"
        />
        {!isHistoryUrl && (
          <SpeedDialAction
            icon={<CasesIcon />}
            onClick={() => {
              setIsCasesDrawerOpen(true);
            }}
            open={isSpeedDialOpen}
            tooltipTitle="Case"
          />
        )}
      </SpeedDialWrapper>
      <SelectDrawer
        handleClose={() => {
          setIsOrganizationsDrawerOpen(false);
        }}
        handleOpen={() => setIsOrganizationsDrawerOpen(true)}
        isOpen={isOrganizationsDrawerOpen}
        label="Organization"
        onChange={(key) => {
          handleOrganizationChange({
            target: { value: key },
          } as unknown as React.ChangeEvent<HTMLInputElement>);
          setIsOrganizationsDrawerOpen(false);
        }}
        options={organizationItems || []}
        value={selectedOrganization?.organizationID || ''}
      />
      <SelectDrawer
        handleClose={() => {
          setIsCasesDrawerOpen(false);
        }}
        handleOpen={() => setIsCasesDrawerOpen(true)}
        isOpen={isCasesDrawerOpen}
        label="Cases"
        onChange={(key) => {
          handleCaseChange({
            target: { value: key },
          } as unknown as React.ChangeEvent<HTMLInputElement>);
          setIsCasesDrawerOpen(false);
        }}
        options={caseItems || []}
        value={selectedCase?.caseID || ''}
      />
    </>
  );
}
